/**
 * useUserData — TanStack Query composable
 *
 * Single source of truth for account/user data.
 * Replaces the Vuex `user` module's `user` state (src/store/modules/user.js).
 *
 * The ['account-settings'] query is pre-populated at startup in
 * src/stores/auth.ts (checkToken). This composable reads from that cache.
 */
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import type { UserData } from '@/shared/types'

export const userDataQueryOptions = {
  queryKey: ['account-settings'] as const,
  // apiInterceptor already unwraps response.data via its response interceptor.
  // The returned value is the payload directly (UserData shape).
  queryFn: async (): Promise<UserData> => {
    return apiInterceptor.get(environment('VUE_APP_API_URL') + 'settings/v1/account/')
  },
  staleTime: Infinity,
}

export function useUserData() {
  const { data, isLoading, isError, error } = useQuery(userDataQueryOptions)

  const user = computed<UserData | undefined>(() => {
    const cached = data.value as any
    // Guard against stale persisted cache that still holds the raw API envelope shape.
    if (cached && typeof cached === 'object' && 'data' in cached && 'success' in cached) return cached.data
    return cached ?? undefined
  })

  return {
    user,
    isLoading,
    isError,
    error,
  }
}
